import { ReactNode, useState, useEffect } from 'react';
import cn from 'classnames';
import { useTranslations } from 'next-intl';
import { usePopper } from 'react-popper';
import styles from './MenuFlyout.module.scss';

export interface MenuFlyoutProps {
  children: ReactNode;
  showPopover: boolean;
  referenceElement: Element | null | undefined;
  type?: string;
  placement?: 'bottom' | 'bottom-end';
  showArrow?: boolean;
}

const MenuFlyout = ({
  children,
  showPopover,
  referenceElement,
  type,
  placement = 'bottom',
  showArrow = true,
}: MenuFlyoutProps) => {
  const t = useTranslations('common');
  const [popperElement, setPopperElement] = useState<HTMLDivElement | null>(
    null
  );

  const [arrowElement, setArrowElement] = useState<HTMLDivElement | null>(null);

  const {
    styles: popperStyles,
    attributes,
    update,
  } = usePopper(referenceElement, popperElement, {
    placement,
    strategy: 'fixed',
    modifiers: [
      { name: 'arrow', options: { element: arrowElement } },
      {
        name: 'offset',
        options: {
          offset: [0, 16],
        },
      },
      {
        name: 'preventOverflow',
        options: {
          padding: 32,
          rootBoundary: 'viewport',
        },
      },
    ],
  });

  useEffect(() => {
    if (showPopover && referenceElement && update) {
      // Use requestAnimationFrame to ensure DOM is ready
      requestAnimationFrame(() => {
        update();
      });
    }
  }, [showPopover, referenceElement, update]);

  // Don't render if referenceElement is not available
  if (!referenceElement && showPopover) {
    return null;
  }

  return (
    <div
      ref={setPopperElement}
      className={cn(
        type === 'account' ? styles.accountPopover : styles.helpPopover,
        {
          [styles.transitionStyles]: showPopover,
        }
      )}
      style={popperStyles.popper}
      role="dialog"
      aria-label={
        type === 'account'
          ? t('nav.utility.account.menu')
          : t('nav.utility.help.menu')
      }
      {...attributes.popper}
    >
      <div
        ref={setArrowElement}
        style={popperStyles.arrow}
        className={cn(styles.popperArrow, {
          [styles.hiddenArrow]: !showArrow,
        })}
      />
      {children}
    </div>
  );
};

export default MenuFlyout;
